/**
 * Payment Record Modal (T104, T110, T111)
 * Record new payment, handle overpayment, and edit existing payments
 */

import { useState, useEffect } from 'react';
import { X, DollarSign, AlertTriangle, Info } from 'lucide-react';
import { useToast } from '../ui';
import {
  useRecordPayment,
  useUpdatePayment,
  formatCurrency,
  PAYMENT_METHOD_LABELS,
  type Payment,
  type PaymentMethod,
  type RecordPaymentInput,
} from '../../hooks/usePayments';

interface PaymentRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceTotal?: number;
  invoiceBalance?: number;
  customerId?: string;
  customerName?: string;
  /** When set, modal enters edit mode */
  editPayment?: Payment | null;
}

const METHODS: PaymentMethod[] = [
  'CREDIT_CARD',
  'DEBIT_CARD',
  'BANK_TRANSFER',
  'CASH',
  'CHECK',
  'APPLE_PAY',
  'GOOGLE_PAY',
  'OTHER',
];

export default function PaymentRecordModal({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  invoiceTotal,
  invoiceBalance,
  customerId: _customerId,
  customerName,
  editPayment,
}: PaymentRecordModalProps) {
  const { showToast } = useToast();
  const recordPayment = useRecordPayment();
  const updatePayment = useUpdatePayment();

  const isEditMode = !!editPayment;

  // Form state
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CREDIT_CARD');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');

  // Overpayment state (T110)
  const [overpaymentDetected, setOverpaymentDetected] = useState(false);
  const [overpaymentAmount, setOverpaymentAmount] = useState(0);
  const [overpaymentResolution, setOverpaymentResolution] = useState<
    'apply_credit' | 'refund' | 'tip' | ''
  >('');

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      if (editPayment) {
        setAmount(editPayment.amount.toString());
        setMethod(editPayment.method);
        setPaymentDate(
          editPayment.paidAt
            ? new Date(editPayment.paidAt).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0]
        );
        setTransactionId(editPayment.transactionId || '');
        setNotes(editPayment.notes || '');
        setCorrectionReason('');
      } else {
        setAmount(invoiceBalance ? invoiceBalance.toString() : '');
        setMethod('CREDIT_CARD');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setTransactionId('');
        setNotes('');
        setCorrectionReason('');
      }
      setOverpaymentDetected(false);
      setOverpaymentAmount(0);
      setOverpaymentResolution('');
    }
  }, [isOpen, editPayment, invoiceBalance]);

  // Detect overpayment on amount change
  useEffect(() => {
    const numAmount = parseFloat(amount);
    if (
      !isEditMode &&
      invoiceBalance !== undefined &&
      numAmount > invoiceBalance &&
      invoiceBalance > 0
    ) {
      setOverpaymentDetected(true);
      setOverpaymentAmount(+(numAmount - invoiceBalance).toFixed(2));
    } else {
      setOverpaymentDetected(false);
      setOverpaymentAmount(0);
      setOverpaymentResolution('');
    }
  }, [amount, invoiceBalance, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount <= 0) {
      showToast('error', 'Please enter a valid amount');
      return;
    }

    if (isEditMode && editPayment) {
      // Edit mode (T111)
      if (!correctionReason.trim()) {
        showToast('error', 'Correction reason is required for edits');
        return;
      }

      try {
        await updatePayment.mutateAsync({
          id: editPayment.id,
          data: {
            amount: numAmount,
            method,
            paymentDate,
            transactionId: transactionId || undefined,
            notes: notes || undefined,
            correctionReason,
          },
        });
        showToast('success', 'Payment updated successfully');
        onClose();
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to update payment';
        showToast('error', msg);
      }
      return;
    }

    // Record new payment
    if (!invoiceId) {
      showToast('error', 'Invoice ID is required');
      return;
    }

    if (overpaymentDetected && !overpaymentResolution) {
      showToast('error', 'Please select how to handle the overpayment');
      return;
    }

    try {
      const input: RecordPaymentInput = {
        invoiceId,
        amount: numAmount,
        method,
        paymentDate,
        transactionId: transactionId || undefined,
        notes: notes || undefined,
      };
      if (overpaymentDetected && overpaymentResolution) {
        input.overpaymentResolution = overpaymentResolution;
      }

      const result = await recordPayment.mutateAsync(input);

      // Check for overpayment response from server
      if (result && !result.success && (result as any).overpaymentAmount) {
        setOverpaymentDetected(true);
        setOverpaymentAmount((result as any).overpaymentAmount);
        return;
      }

      showToast('success', 'Payment recorded successfully');
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.overpaymentAmount) {
        setOverpaymentDetected(true);
        setOverpaymentAmount(data.overpaymentAmount);
        return;
      }
      showToast('error', data?.error || 'Failed to record payment');
    }
  };

  if (!isOpen) {
    return null;
  }

  const isPending = recordPayment.isPending || updatePayment.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background rounded-xl shadow-xl border border-border w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-success" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {isEditMode ? 'Edit Payment' : 'Record Payment'}
              </h2>
              {invoiceNumber && (
                <p className="text-sm text-muted-foreground">
                  Invoice {invoiceNumber}
                  {customerName && ` — ${customerName}`}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="claude-button claude-button-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Invoice summary */}
        {!isEditMode && invoiceTotal !== undefined && (
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3 text-sm">
              <span className="text-muted-foreground">Invoice Total</span>
              <span className="font-semibold">{formatCurrency(invoiceTotal)}</span>
            </div>
            {invoiceBalance !== undefined && invoiceBalance !== invoiceTotal && (
              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3 mt-2 text-sm">
                <span className="text-muted-foreground">Remaining Balance</span>
                <span className="font-semibold text-warning">{formatCurrency(invoiceBalance)}</span>
              </div>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Amount *</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="claude-input pl-9 w-full"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Overpayment alert (T110) */}
          {overpaymentDetected && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-warning">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <p className="font-medium text-sm">
                  Overpayment of {formatCurrency(overpaymentAmount)} detected
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                How should the excess amount be handled?
              </p>
              <div className="space-y-2">
                {(
                  [
                    {
                      value: 'apply_credit',
                      label: 'Apply as credit',
                      desc: 'Store as credit for future invoices',
                    },
                    {
                      value: 'refund',
                      label: 'Refund overpayment',
                      desc: 'Create a refund record for the excess',
                    },
                    {
                      value: 'tip',
                      label: 'Mark as tip',
                      desc: 'Record the excess as a gratuity',
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      overpaymentResolution === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="overpaymentResolution"
                      value={opt.value}
                      checked={overpaymentResolution === opt.value}
                      onChange={(e) =>
                        setOverpaymentResolution(
                          e.target.value as 'apply_credit' | 'refund' | 'tip'
                        )
                      }
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-medium">{opt.label}</span>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Payment Method */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Payment Method *</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              className="claude-input w-full"
              required
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Payment Date *</label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="claude-input w-full"
              required
            />
          </div>

          {/* Reference / Transaction ID */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Reference Number</label>
            <input
              type="text"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="claude-input w-full"
              placeholder="Transaction or reference number"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="claude-input w-full"
              rows={2}
              placeholder="Optional notes..."
            />
          </div>

          {/* Correction Reason (edit mode only — T111) */}
          {isEditMode && (
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                Correction Reason *
                <Info className="w-3.5 h-3.5 text-muted-foreground" />
              </label>
              <textarea
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                className="claude-input w-full"
                rows={2}
                placeholder="Explain why this payment is being edited..."
                required
              />
              <p className="text-xs text-muted-foreground">
                This will be recorded in the audit trail.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="claude-button claude-button-secondary"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="claude-button claude-button-primary"
              disabled={isPending}
            >
              {isPending
                ? isEditMode
                  ? 'Saving...'
                  : 'Recording...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
